import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import supabase from '../../../config/supabaseClient';

// Criar o contexto
const FossasSepticasContext = createContext();

// Hook personalizado para usar o contexto
export const useFossasSepticas = () => {
  const context = useContext(FossasSepticasContext);
  if (!context) {
    throw new Error('useFossasSepticas deve ser usado dentro de um FossasSepticasProvider');
  }
  return context;
};

// Provider Component
export const FossasSepticasProvider = ({ children }) => {
  // Estados
  const [propriedades, setPropriedades] = useState([]);
  const [residencias, setResidencias] = useState([]);
  const [fossas, setFossas] = useState([]);
  const [agentes, setAgentes] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [visitas, setVisitas] = useState([]);
  const [loading, setLoading] = useState({
    propriedades: false,
    residencias: false,
    fossas: false,
    agentes: false,
    localidades: false,
    visitas: false
  });
  const [filtros, setFiltros] = useState({
    status: null,
    localidade: null,
    agente: null,
    temFossa: null
  });
  const [estatisticas, setEstatisticas] = useState({
    propriedades: {
      total: 0,
      mapeadas: 0,
      naoMapeadas: 0,
      porLocalidade: []
    },
    residencias: {
      total: 0,
      mapeadas: 0,
      naoMapeadas: 0,
      comFossa: 0,
      semFossa: 0
    }
  });

  // Carregar localidades
  const carregarLocalidades = useCallback(async () => {
    setLoading(prev => ({ ...prev, localidades: true }));
    
    try {
      const { data, error } = await supabase
        .from('ccbairros')
        .select('*')
        .order('descricao');
      
      if (error) throw error;
      
      setLocalidades(data || []);
    } catch (error) {
      console.error('Erro ao carregar localidades:', error);
      toast.error('Erro ao carregar localidades');
    } finally {
      setLoading(prev => ({ ...prev, localidades: false }));
    }
  }, []);

  // Carregar agentes
  const carregarAgentes = useCallback(async () => {
    setLoading(prev => ({ ...prev, agentes: true }));
    
    try {
      // Carregar agentes
      const { data, error } = await supabase
        .from('agentes')
        .select('*')
        .order('nome');
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        setAgentes([]);
        return;
      }
      
      // Carregar propriedades para contar quantas estão atribuídas a cada agente
      const { data: propriedadesData, error: propError } = await supabase
        .from('propriedades_rurais')
        .select('id, agente_id');
      
      if (propError) throw propError;
      
      // Agrupar propriedades por agente_id
      const propriedadesPorAgente = {};
      propriedadesData.forEach(prop => {
        if (prop.agente_id) {
          if (!propriedadesPorAgente[prop.agente_id]) {
            propriedadesPorAgente[prop.agente_id] = [];
          }
          propriedadesPorAgente[prop.agente_id].push(prop);
        }
      });
      
      // Adicionar propriedades aos agentes
      const agentesComPropriedades = data.map(agente => ({
        ...agente,
        propriedades: propriedadesPorAgente[agente.id] || []
      }));
      
      setAgentes(agentesComPropriedades);
    } catch (error) {
      console.error('Erro ao carregar agentes:', error);
      toast.error('Erro ao carregar agentes');
    } finally {
      setLoading(prev => ({ ...prev, agentes: false }));
    }
  }, []);

  // Carregar propriedades
  const carregarPropriedades = useCallback(async () => {
    setLoading(prev => ({ ...prev, propriedades: true }));
    
    try {
      // Carregar propriedades
      let query = supabase
        .from('propriedades_rurais')
        .select(`
          *,
          localidade:ccbairros(id, descricao),
          agente:agentes(id, nome)
        `)
        .order('created_at', { ascending: false });
      
      // Aplicar filtros
      if (filtros.status) {
        query = query.eq('status', filtros.status);
      }
      
      if (filtros.localidade) {
        query = query.eq('localidade_id', filtros.localidade);
      }
      
      if (filtros.agente) {
        query = query.eq('agente_id', filtros.agente);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Se não temos propriedades, não precisamos continuar
      if (!data || data.length === 0) {
        setPropriedades([]);
        atualizarEstatisticas([]);
        return;
      }
      
      // Extrair IDs das propriedades
      const propriedadeIds = data.map(p => p.id);
      
      // Carregar residências para estas propriedades
      const { data: residenciasData, error: residenciasError } = await supabase
        .from('residencias')
        .select('*')
        .in('propriedade_id', propriedadeIds);
      
      if (residenciasError) throw residenciasError;
      
      // Carregar fossas sépticas
      const { data: fossasData, error: fossasError } = await supabase
        .from('fossas_septicas')
        .select('*, residencia:residencias(propriedade_id)')
        .not('residencia', 'is', null);
      
      if (fossasError) throw fossasError;
      
      // Agrupar residências por propriedade_id
      const residenciasPorPropriedade = {};
      residenciasData.forEach(residencia => {
        if (!residenciasPorPropriedade[residencia.propriedade_id]) {
          residenciasPorPropriedade[residencia.propriedade_id] = [];
        }
        residenciasPorPropriedade[residencia.propriedade_id].push(residencia);
      });
      
      // Agrupar fossas por propriedade_id
      const fossasPorPropriedade = {};
      fossasData.forEach(fossa => {
        const propriedadeId = fossa.residencia?.propriedade_id;
        if (propriedadeId) {
          if (!fossasPorPropriedade[propriedadeId]) {
            fossasPorPropriedade[propriedadeId] = [];
          }
          fossasPorPropriedade[propriedadeId].push(fossa);
        }
      });
      
      // Adicionar residências e fossas às propriedades
      const propriedadesComRelacionamentos = data.map(propriedade => ({
        ...propriedade,
        residencias: residenciasPorPropriedade[propriedade.id] || [],
        fossas: fossasPorPropriedade[propriedade.id] || []
      }));
      
      setPropriedades(propriedadesComRelacionamentos);
      
      // Atualizar estatísticas
      atualizarEstatisticas(propriedadesComRelacionamentos);
    } catch (error) {
      console.error('Erro ao carregar propriedades:', error);
      toast.error('Erro ao carregar propriedades');
    } finally {
      setLoading(prev => ({ ...prev, propriedades: false }));
    }
  }, [filtros]);

  // Carregar residências
  const carregarResidencias = useCallback(async (propriedadeId = null) => {
    setLoading(prev => ({ ...prev, residencias: true }));
    
    try {
      let query = supabase
        .from('residencias')
        .select(`
          *,
          propriedade:propriedades_rurais(id, nome)
        `)
        .order('created_at', { ascending: false });
      
      if (propriedadeId) {
        query = query.eq('propriedade_id', propriedadeId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setResidencias(data || []);
      
      // Atualizar estatísticas de residências
      atualizarEstatisticasResidencias(data || []);
    } catch (error) {
      console.error('Erro ao carregar residências:', error);
      toast.error('Erro ao carregar residências');
    } finally {
      setLoading(prev => ({ ...prev, residencias: false }));
    }
  }, []);

  // Carregar fossas
  const carregarFossas = useCallback(async (residenciaId = null) => {
    setLoading(prev => ({ ...prev, fossas: true }));
    
    try {
      let query = supabase
        .from('fossas_septicas')
        .select(`
          *,
          residencia:residencias(id, identificacao, propriedade_id)
        `)
        .order('created_at', { ascending: false });
      
      if (residenciaId) {
        query = query.eq('residencia_id', residenciaId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setFossas(data || []);
    } catch (error) {
      console.error('Erro ao carregar fossas:', error);
      toast.error('Erro ao carregar fossas sépticas');
    } finally {
      setLoading(prev => ({ ...prev, fossas: false }));
    }
  }, []);

  // Carregar visitas
  const carregarVisitas = useCallback(async (propriedadeId = null, residenciaId = null) => {
    setLoading(prev => ({ ...prev, visitas: true }));
    
    try {
      let query = supabase
        .from('visitas')
        .select(`
          *,
          propriedade:propriedades_rurais(id, nome),
          residencia:residencias(id, identificacao),
          agente:agentes(id, nome)
        `)
        .order('data_visita', { ascending: false });
      
      if (propriedadeId) {
        query = query.eq('propriedade_id', propriedadeId);
      }
      
      if (residenciaId) {
        query = query.eq('residencia_id', residenciaId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setVisitas(data || []);
    } catch (error) {
      console.error('Erro ao carregar visitas:', error);
      toast.error('Erro ao carregar histórico de visitas');
    } finally {
      setLoading(prev => ({ ...prev, visitas: false }));
    }
  }, []);

  // Atualizar estatísticas de propriedades
  const atualizarEstatisticas = useCallback(async (propriedadesData = null) => {
    try {
      // Se não recebeu dados de propriedades, usa o estado atual
      const props = propriedadesData || propriedades;
      
      // Contagem por status
      const mapeadas = props.filter(p => p.status === 'Mapeada').length;
      const naoMapeadas = props.filter(p => p.status === 'Não Mapeada').length;
      
      // Contagem por localidade
      const porLocalidade = [];
      const localidadesMap = {};
      
      props.forEach(prop => {
        const localidadeId = prop.localidade_id;
        const localidadeNome = prop.localidade?.descricao || 'Desconhecida';
        
        if (!localidadesMap[localidadeId]) {
          localidadesMap[localidadeId] = {
            id: localidadeId,
            nome: localidadeNome,
            total: 0,
            mapeadas: 0,
            naoMapeadas: 0
          };
        }
        
        localidadesMap[localidadeId].total++;
        
        if (prop.status === 'Mapeada') {
          localidadesMap[localidadeId].mapeadas++;
        } else if (prop.status === 'Não Mapeada') {
          localidadesMap[localidadeId].naoMapeadas++;
        }
      });
      
      // Converter o mapa em array
      Object.values(localidadesMap).forEach(loc => {
        porLocalidade.push(loc);
      });
      
      // Atualizar o estado de estatísticas
      setEstatisticas(prev => ({
        ...prev,
        propriedades: {
          total: props.length,
          mapeadas,
          naoMapeadas,
          porLocalidade
        }
      }));
    } catch (error) {
      console.error('Erro ao atualizar estatísticas de propriedades:', error);
    }
  }, [propriedades]);

  // Atualizar estatísticas de residências
  const atualizarEstatisticasResidencias = useCallback(async (residenciasData = null) => {
    try {
      // Se não recebeu dados de residências, usa o estado atual
      const resids = residenciasData || residencias;
      
      // Contagem por status
      const mapeadas = resids.filter(r => r.status === 'Mapeada').length;
      const naoMapeadas = resids.filter(r => r.status === 'Não Mapeada').length;
      
      // Buscar contagem de residências com fossas
      const { count: comFossaCount, error } = await supabase
        .from('fossas_septicas')
        .select('residencia_id', { count: 'exact', head: true })
        .not('residencia_id', 'is', null);
      
      if (error) throw error;
      
      const comFossa = comFossaCount || 0;
      const semFossa = resids.length - comFossa;
      
      // Atualizar o estado de estatísticas
      setEstatisticas(prev => ({
        ...prev,
        residencias: {
          total: resids.length,
          mapeadas,
          naoMapeadas,
          comFossa,
          semFossa
        }
      }));
    } catch (error) {
      console.error('Erro ao atualizar estatísticas de residências:', error);
    }
  }, [residencias]);

  // Salvar propriedade
  const salvarPropriedade = useCallback(async (propriedade) => {
    try {
      const { id, ...dadosPropriedade } = propriedade;
      
      // Converter campos vazios para null
      if (dadosPropriedade.agente_id === '') {
        dadosPropriedade.agente_id = null;
      }
      if (dadosPropriedade.localidade_id === '') {
        dadosPropriedade.localidade_id = null;
      }
      
      if (id) {
        // Atualizar propriedade existente
        const { error } = await supabase
          .from('propriedades_rurais')
          .update(dadosPropriedade)
          .eq('id', id);
        
        if (error) throw error;
        
        toast.success('Propriedade atualizada com sucesso');
      } else {
        // Criar nova propriedade
        const { error } = await supabase
          .from('propriedades_rurais')
          .insert([dadosPropriedade]);
        
        if (error) throw error;
        
        toast.success('Propriedade cadastrada com sucesso');
      }
      
      // Recarregar propriedades
      await carregarPropriedades();
      
      return true;
    } catch (error) {
      console.error('Erro ao salvar propriedade:', error);
      toast.error('Erro ao salvar propriedade');
      return false;
    }
  }, [carregarPropriedades]);

  // Salvar residência
  const salvarResidencia = useCallback(async (residencia) => {
    try {
      const { id, ...dadosResidencia } = residencia;
      
      if (id) {
        // Atualizar residência existente
        const { error } = await supabase
          .from('residencias')
          .update(dadosResidencia)
          .eq('id', id);
        
        if (error) throw error;
        
        toast.success('Residência atualizada com sucesso');
      } else {
        // Criar nova residência
        const { error } = await supabase
          .from('residencias')
          .insert([dadosResidencia]);
        
        if (error) throw error;
        
        toast.success('Residência cadastrada com sucesso');
      }
      
      // Recarregar residências
      await carregarResidencias(residencia.propriedade_id);
      
      return true;
    } catch (error) {
      console.error('Erro ao salvar residência:', error);
      toast.error('Erro ao salvar residência');
      return false;
    }
  }, [carregarResidencias]);

  // Salvar agente
  const salvarAgente = useCallback(async (agente) => {
    try {
      // Extrair apenas os campos que são colunas na tabela agentes
      // Isso evita tentar salvar campos que não existem na tabela
      const { id, nome, cpf, telefone, email, ativo } = agente;
      
      // Dados a serem salvos (sem campos adicionais)
      const dadosAgente = { nome, cpf, telefone, email, ativo };
      
      if (id) {
        // Atualizar agente existente
        const { error } = await supabase
          .from('agentes')
          .update(dadosAgente)
          .eq('id', id);
        
        if (error) throw error;
        
        toast.success('Agente atualizado com sucesso');
      } else {
        // Criar novo agente
        const { error } = await supabase
          .from('agentes')
          .insert([dadosAgente]);
        
        if (error) throw error;
        
        toast.success('Agente cadastrado com sucesso');
      }
      
      // Recarregar agentes
      await carregarAgentes();
      
      return true;
    } catch (error) {
      console.error('Erro ao salvar agente:', error);
      toast.error('Erro ao salvar agente');
      return false;
    }
  }, [carregarAgentes]);

  // Salvar localidade
  const salvarLocalidade = useCallback(async (localidade) => {
    try {
      const { id, ...dadosLocalidade } = localidade;
      
      // Converter campos vazios para null
      if (dadosLocalidade.latitude === '') {
        dadosLocalidade.latitude = null;
      }
      if (dadosLocalidade.longitude === '') {
        dadosLocalidade.longitude = null;
      }
      
      if (id) {
        // Atualizar localidade existente
        const { error } = await supabase
          .from('ccbairros')
          .update(dadosLocalidade)
          .eq('id', id);
        
        if (error) throw error;
        
        toast.success('Localidade atualizada com sucesso');
      } else {
        // Criar nova localidade
        const { error } = await supabase
          .from('ccbairros')
          .insert([dadosLocalidade]);
        
        if (error) throw error;
        
        toast.success('Localidade cadastrada com sucesso');
      }
      
      // Recarregar localidades
      await carregarLocalidades();
      
      return true;
    } catch (error) {
      console.error('Erro ao salvar localidade:', error);
      toast.error('Erro ao salvar localidade');
      return false;
    }
  }, [carregarLocalidades]);

  // Registrar visita
  const registrarVisita = useCallback(async (visita) => {
    try {
      const { error } = await supabase
        .from('visitas')
        .insert([visita]);
      
      if (error) throw error;
      
      toast.success('Visita registrada com sucesso');
      
      // Recarregar visitas para a propriedade
      await carregarVisitas(visita.propriedade_id, visita.residencia_id);
      
      return true;
    } catch (error) {
      console.error('Erro ao registrar visita:', error);
      toast.error('Erro ao registrar visita');
      return false;
    }
  }, [carregarVisitas]);

  // Cadastrar fossa séptica
  const cadastrarFossa = useCallback(async (fossa) => {
    try {
      // Inserir fossa
      const { error: fossaError } = await supabase
        .from('fossas_septicas')
        .insert([fossa]);
      
      if (fossaError) throw fossaError;
      
      // Atualizar status da residência para "Mapeada"
      const { error: residError } = await supabase
        .from('residencias')
        .update({ status: 'Mapeada' })
        .eq('id', fossa.residencia_id);
      
      if (residError) throw residError;
      
      toast.success('Fossa séptica cadastrada com sucesso');
      
      // Buscar a propriedade_id da residência
      const { data: residenciaData, error: residFetchError } = await supabase
        .from('residencias')
        .select('propriedade_id')
        .eq('id', fossa.residencia_id)
        .single();
      
      if (residFetchError) throw residFetchError;
      
      // Recarregar fossas e residências
      await carregarFossas(fossa.residencia_id);
      await carregarResidencias(residenciaData.propriedade_id);
      
      return true;
    } catch (error) {
      console.error('Erro ao cadastrar fossa séptica:', error);
      toast.error('Erro ao cadastrar fossa séptica');
      return false;
    }
  }, [carregarFossas, carregarResidencias]);

  // Atribuir propriedade a agente
  const atribuirPropriedade = useCallback(async (propriedadeId, agenteId) => {
    try {
      const { error } = await supabase
        .from('propriedades_rurais')
        .update({ agente_id: agenteId })
        .eq('id', propriedadeId);
      
      if (error) throw error;
      
      toast.success('Propriedade atribuída com sucesso');
      
      // Recarregar propriedades
      await carregarPropriedades();
      
      return true;
    } catch (error) {
      console.error('Erro ao atribuir propriedade:', error);
      toast.error('Erro ao atribuir propriedade');
      return false;
    }
  }, [carregarPropriedades]);

  // Carregar dados iniciais
  useEffect(() => {
    carregarLocalidades();
    carregarAgentes();
  }, [carregarLocalidades, carregarAgentes]);

  // Carregar propriedades quando os filtros mudarem
  useEffect(() => {
    carregarPropriedades();
  }, [carregarPropriedades, filtros]);

  // Valor do contexto
  const value = {
    // Estados
    propriedades,
    residencias,
    fossas,
    agentes,
    localidades,
    visitas,
    loading,
    filtros,
    estatisticas,
    
    // Ações
    setFiltros,
    carregarPropriedades,
    carregarResidencias,
    carregarFossas,
    carregarVisitas,
    carregarAgentes,
    carregarLocalidades,
    salvarPropriedade,
    salvarResidencia,
    salvarAgente,
    salvarLocalidade,
    registrarVisita,
    cadastrarFossa,
    atribuirPropriedade,
    atualizarEstatisticas,
    atualizarEstatisticasResidencias
  };

  return (
    <FossasSepticasContext.Provider value={value}>
      {children}
    </FossasSepticasContext.Provider>
  );
};

export default FossasSepticasProvider;